/**
 * SF-171: narrowed during Phase 3 consolidation to a single genuinely-presentational helper.
 * `types/intel.ts` already declares `CrystalBallData`/`SpyOnMilitaryData`/`SurveyData` as plain-
 * number interfaces with a working `parseIntelData()` discriminated-union mapper — this module
 * does not reinvent that. `buildSurveyRows()` is the building-row label/sort/filter/percent logic
 * previously inline in `IntelExpandedRow.tsx`'s `SVPanel`, extracted so both desktop's `SVPanel`
 * and the new `MobileSurveyCard` compute identical rows from one place.
 */

export interface SurveyBuildingRow {
  key: string;
  label: string;
  count: number;
  pct: number;
}

const BUILDING_LABELS: Record<string, string> = {
  NONE: 'Barren Land', HOME: 'Homes', FARM: 'Farms', MILL: 'Mills', BANK: 'Banks',
  TRAINING_GROUND: 'Training Grounds', ARMOURY: 'Armouries', MILITARY_BARRACK: 'Barracks',
  FORT: 'Forts', GUILD: 'Guilds', TOWER: 'Towers',
  THIEVES_DEN: "Thieves' Dens", WATCH_TOWER: 'Watch Towers', SCHOOL: 'Schools',
  LIBRARY: 'Libraries', HOSPITAL: 'Hospitals', GUARD_STATION: 'Guard Stations',
  STABLE: 'Stables', DUNGEON: 'Dungeons',
};

/**
 * Building-count rows for a Survey panel/card: labeled, zero-count entries filtered out, sorted
 * descending by count, with each row's share of `totalLand` as a rounded percent. Falls back to a
 * title-cased raw key for any building type not in `BUILDING_LABELS`.
 */
export function buildSurveyRows(buildings: Record<string, number>, totalLand: number): SurveyBuildingRow[] {
  return Object.entries(buildings)
    .map(([key, count]) => ({
      key,
      label: BUILDING_LABELS[key] ?? key.replace(/_/g, ' '),
      count,
      pct: totalLand > 0 ? Math.round((count / totalLand) * 100) : 0,
    }))
    .filter((b) => b.count > 0)
    .sort((a, b) => b.count - a.count);
}
